import React from 'react';
import {
  Avatar, Box, Chip, Dialog, DialogContent,
  Divider, IconButton, Stack, Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { AccessTime, Assignment, Close, OpenInNew } from '@mui/icons-material';
import { format } from 'date-fns';
import { flatFormData, extractImages, getLocation, prettyKey } from '../utils';

/**
 * Detailed modal for a single task / form entry.
 * Shows synced status, form data fields, photos, and GPS location.
 *
 * Props:
 *   entry   – task entry object
 *   open    – boolean
 *   onClose – close handler
 */
export default function EntryModal({ entry, open, onClose }) {
  if (!entry) return null;

  const flat    = flatFormData(entry.formData);
  const imgs    = extractImages(entry.formData);
  const loc     = getLocation(entry);
  const author  = entry.author;

  const textFields = Object.entries(flat).filter(([k, v]) => {
    if (k === 'location') return false;
    if (Array.isArray(v) && v[0]?.url) return false;
    if (v && typeof v === 'object' && v.url) return false;
    return true;
  });

  return (
    <Dialog
      open={open} onClose={onClose} maxWidth={false}
      PaperProps={{
        sx: {
          width: { xs: '98vw', md: '780px' },
          maxHeight: '90vh', borderRadius: '20px',
          background: '#fff',
          boxShadow: '0 24px 64px rgba(15,33,55,0.18)',
          zIndex: 1400,
        },
      }}
    >
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', maxHeight: '90vh' }}>

        {/* ── Header ── */}
        <Box sx={{
          p: 3, flexShrink: 0,
          background: 'linear-gradient(135deg, rgba(16,117,186,0.08), rgba(0,168,130,0.04))',
          borderBottom: '1px solid rgba(16,117,186,0.1)', position: 'relative',
        }}>
          <IconButton
            onClick={onClose} size="small"
            sx={{ position: 'absolute', top: 12, right: 12, color: '#4a6a85' }}
          >
            <Close fontSize="small" />
          </IconButton>

          <Stack direction="row" spacing={2} alignItems="flex-start">
            {/* Icon */}
            <Box sx={{
              width: 48, height: 48, borderRadius: '12px', flexShrink: 0,
              background: 'linear-gradient(135deg, #91ce0f, #5f940a)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(16,117,186,0.3)',
            }}>
              <Assignment sx={{ color: '#fff', fontSize: 24 }} />
            </Box>

            {/* Title + chips */}
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f2137', lineHeight: 1.2, mb: 0.5 }}>
                {entry.taskLabel || entry.taskId}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  label={entry.synced ? 'Synced' : 'Offline'} size="small"
                  sx={{
                    background: entry.synced ? '#e6f9f4' : '#fff4e0',
                    color:      entry.synced ? '#0a9e74' : '#c06000',
                    border:     `1px solid ${entry.synced ? '#b3ead9' : '#ffd599'}`,
                    fontWeight: 700, fontSize: '0.7rem', height: 22,
                  }}
                />
                {author && (
                  <Chip
                    avatar={<Avatar sx={{ width: 18, height: 18, fontSize: '0.6rem' }}>{author.firstName?.[0]}</Avatar>}
                    label={`${author.firstName} ${author.lastName}`}
                    size="small"
                    sx={{ background: '#f0f8f4', color: '#0f2137', fontSize: '0.7rem', height: 22 }}
                  />
                )}
                {entry.createdAt && (
                  <Chip
                    icon={<AccessTime sx={{ fontSize: '12px !important' }} />}
                    label={format(new Date(entry.createdAt), 'MMM d, yyyy hh:mm a')}
                    size="small"
                    sx={{ background: '#f0f8f4', color: '#4a6a85', fontSize: '0.7rem', height: 22 }}
                  />
                )}
              </Stack>
            </Box>
          </Stack>
        </Box>

        {/* ── Body ── */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
          <Stack spacing={3}>

            {/* Form fields */}
            {textFields.length > 0 && (
              <Box>
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#91ce0f', mb: 1.5 }}>
                  Form Data
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 1.5 }}>
                  {textFields.map(([k, v]) => (
                    <Box key={k} sx={{ background: '#f5f8fc', borderRadius: '10px', px: 1.75, py: 1.25, border: '1px solid rgba(16,117,186,0.1)' }}>
                      <Typography sx={{ fontSize: '0.67rem', fontWeight: 700, color: '#4a6a85', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.25 }}>
                        {prettyKey(k)}
                      </Typography>
                      <Typography sx={{ fontSize: '0.88rem', color: '#0f2137', fontWeight: 500 }}>
                        {String(v)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Photos */}
            {imgs.length > 0 && (
              <>
                <Divider sx={{ borderColor: 'rgba(16,117,186,0.1)' }} />
                <Box>
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#91ce0f', mb: 1.5 }}>
                    Photos
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 1.5 }}>
                    {imgs.map((img, i) => (
                      <Box
                        key={i} component="a" href={img.url} target="_blank" rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          borderRadius: '12px', overflow: 'hidden', position: 'relative',
                          border: '1px solid rgba(16,117,186,0.15)',
                          boxShadow: '0 2px 8px rgba(15,33,55,0.08)', cursor: 'zoom-in',
                          '&:hover .ph-over': { opacity: 1 },
                          '&:hover img': { transform: 'scale(1.04)' },
                        }}
                      >
                        <Box component="img" src={img.url} alt={img.label}
                          sx={{ width: '100%', height: 130, objectFit: 'cover', display: 'block', transition: 'transform 0.3s' }} />
                        <Box className="ph-over" sx={{
                          position: 'absolute', inset: 0, background: 'rgba(16,117,186,0.3)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          opacity: 0, transition: 'opacity 0.2s',
                        }}>
                          <OpenInNew sx={{ color: '#fff', fontSize: 22 }} />
                        </Box>
                        <Box sx={{
                          position: 'absolute', bottom: 0, left: 0, right: 0,
                          background: 'linear-gradient(transparent, rgba(0,0,0,0.65))',
                          px: 1, py: 0.75,
                        }}>
                          <Typography sx={{ fontSize: '0.68rem', color: '#fff', fontWeight: 600 }}>
                            {prettyKey(img.label)}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </>
            )}

            {/* Location */}
            {loc && (
              <>
                <Divider sx={{ borderColor: 'rgba(16,117,186,0.1)' }} />
                <Box>
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#91ce0f', mb: 1.5 }}>
                    Location
                  </Typography>
                  <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                    {[
                      { l: 'Latitude',  v: loc.lat },
                      { l: 'Longitude', v: loc.lng },
                      ...(entry.formData?.location?.accuracy != null
                        ? [{ l: 'Accuracy', v: `${parseFloat(entry.formData.location.accuracy).toFixed(1)} m` }]
                        : []),
                    ].map(({ l, v }) => (
                      <Box key={l} sx={{ background: '#f5f8fc', borderRadius: '10px', px: 2, py: 1.25, border: '1px solid rgba(16,117,186,0.1)' }}>
                        <Typography sx={{ fontSize: '0.68rem', color: '#4a6a85', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.25 }}>{l}</Typography>
                        <Typography sx={{ fontSize: '0.83rem', color: '#0f2137', fontFamily: 'monospace' }}>{v}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </>
            )}

          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
